import React from 'react';
import { motion } from 'framer-motion';
import { FiShield, FiBriefcase, FiTrendingUp, FiUsers, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const features = [
  {
    name: 'Proven Frameworks',
    description: 'Stop guessing. We provide tested blueprints and step-by-step systems to help you successfully acquire and flip land.',
    icon: FiBriefcase,
  },
  {
    name: 'Expert Coaching',
    description: 'Learn directly from industry experts who have closed hundreds of deals. Avoid costly mistakes with our mentorship.',
    icon: FiTrendingUp,
  },
  {
    name: 'Exclusive Community',
    description: 'Network with like-minded real estate entrepreneurs. Share resources, partner on deals, and grow together.',
    icon: FiUsers,
  },
  {
    name: 'Trusted Education',
    description: 'Toptier Ventures LLC is a legitimate business dedicated to your success in the Real Estate Education industry.',
    icon: FiShield,
  }
];

const Benefits = () => {
  return (
    <section className="py-32 bg-white overflow-hidden" id="benefits">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-20 items-center">
          
          {/* Left Column: Image & Testimonial */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="mb-16 lg:mb-0 relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/20 aspect-[4/5] lg:aspect-auto lg:h-[600px]">
              <img 
                src="https://images.unsplash.com/photo-1500076656116-558758c991c1?q=80&w=2500&auto=format&fit=crop" 
                alt="Beautiful raw land" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent"></div>
              
              {/* Floating Testimonial Card */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-white/20"
              >
                <div className="flex gap-1 text-emerald-500 mb-3">
                  {[1, 2, 3, 4, 5].map(i => <SafeIcon key={i} icon={FiCheck} className="text-sm bg-emerald-100 rounded-full p-0.5" />)}
                </div>
                <p className="text-slate-800 font-medium italic mb-4">
                  "Toptier Ventures LLC completely changed my approach to real estate. Their training helped me flip my first parcel of land within 60 days of starting the program."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold">
                    MR
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">Michael R.</p>
                    <p className="text-xs text-slate-500">Successful Student</p>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Decorative dots */}
            <div className="absolute -top-8 -left-8 w-32 h-32 bg-[radial-gradient(#10b981_2px,transparent_2px)] [background-size:16px_16px] opacity-20 -z-10"></div>
          </motion.div>

          {/* Right Column: Copy & Features */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight sm:text-5xl mb-6 leading-tight">
              Why Learn With <br/><span className="text-emerald-600">Toptier Ventures LLC?</span>
            </h2>
            <p className="text-xl text-slate-600 mb-12 font-light leading-relaxed">
              We provide products and services specifically designed to help our students make money. Skip the trial and error and learn from those who have paved the way.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
              {features.map((feature, index) => (
                <motion.div 
                  key={feature.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-5 transition-all duration-300 group-hover:bg-emerald-600 group-hover:shadow-lg group-hover:shadow-emerald-600/20">
                    <SafeIcon icon={feature.icon} className="text-2xl text-emerald-600 transition-colors duration-300 group-hover:text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.name}</h3>
                  <p className="text-slate-500 leading-relaxed font-light">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Benefits;